import { useEffect, useRef, useState } from 'react';
import {
  actionsToOps,
  assistWithCheck,
  type FixTarget,
  checkProposal,
  projectContext,
  redactProject,
  sketchToBoard,
  type ActionResult,
  type AiService,
  type AssistAction,
  type AssistMessage,
  type FeedbackConsent,
  type ImageMediaType,
  type SharedAnswer,
  type Sketch,
  isAiUnavailable,
} from '@elec/ai';
import type { Id, Op, Project } from '@elec/domain';
import type { Finding } from '@elec/rules';
import { pick as pickText, say, type Locale, type Translate } from '@elec/i18n';
import type { Platform } from '@elec/platform';
import { PROJECT_TEMPLATES, type ProjectTemplate } from '@elec/samples';
import { prepareAiImage } from './photos.ts';
import { Dialog, Icon } from './ui.tsx';
import { MAX_SECONDS, micSupported, startRecording, type Recording } from './voice.ts';

/** Pick a starting project. */
export function TemplateDialog(props: {
  t: Translate;
  locale: Locale;
  onClose(): void;
  onCreate(tpl: ProjectTemplate): void;
}) {
  const { t, locale } = props;
  const [pick, setPick] = useState(PROJECT_TEMPLATES[0]!.id);
  const tpl = PROJECT_TEMPLATES.find((x) => x.id === pick)!;
  return (
    <Dialog
      title={t('tpl.title')}
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          <button className="primary" onClick={() => props.onCreate(tpl)} data-testid="template-create">
            {t('tpl.create')}
          </button>
        </>
      }
    >
      <div className="template-list" role="radiogroup">
        {PROJECT_TEMPLATES.map((x) => (
          <label
            key={x.id}
            className={`template${x.id === pick ? ' active' : ''}`}
            data-testid={`template-${x.id}`}
          >
            <input type="radio" name="template" checked={x.id === pick} onChange={() => setPick(x.id)} />
            <span>
              <strong>{pickText(x.name, locale)}</strong>
              <span className="small">{pickText(x.description, locale)}</span>
            </span>
          </label>
        ))}
      </div>
    </Dialog>
  );
}

interface Turn {
  role: 'user' | 'assistant';
  text: string;
  /** The finding a "Fix with the assistant" request is about. */
  fix?: FixTarget;
  result?: ActionResult;
  /** Rule findings the proposal would add (after one automatic revision by the model). */
  findings?: Finding[];
  applied?: boolean;
  /** The proposal's actions, and which of them the user keeps (all by default). */
  actions?: AssistAction[];
  excluded?: number[];
  /** What the answer was made from, for the 👎 feedback the user may choose to share. */
  source?: { messages: AssistMessage[]; context: string; project: Project };
}

/**
 * Chat assistant, in the sidebar: each answer comes with a preview of the changes, applied on demand
 * as one undo step. It stays mounted when its section is folded, so the conversation is kept.
 * With a speech server, the microphone dictates into the message box; the user checks it, then sends.
 */
export function AssistantPanel(props: {
  ai: AiService;
  t: Translate;
  locale: 'fr' | 'en' | 'nl';
  /** Interface language (German included), for the texts around the assistant's answers. */
  uiLocale?: Locale;
  project: Project;
  sheetId: Id;
  mod: string;
  /** App version and platform ("0.12.0 ios"), sent with feedback to tell versions apart. */
  app?: string;
  onApply(ops: Op[], sheetId: Id | undefined): void;
  /** Show a proposal on the canvas without applying it (null: stop). */
  onPreview?(ops: Op[] | null, sheetId: Id | undefined): void;
  /** A request sent from elsewhere (a finding's "Fix with the assistant"); `n` changes each time. */
  request?: { text: string; n: number; fix?: FixTarget };
}) {
  const { ai, t } = props;
  const [turns, setTurns] = useState<Turn[]>([]);
  /** Proposal shown on the canvas: hovered, focused, or pinned with its Preview button (touch). */
  const [shown, setShown] = useState<{ i: number; pinned: boolean } | null>(null);
  const onPreview = useRef(props.onPreview);
  onPreview.current = props.onPreview;
  const show = (i: number | null, pinned = false) => {
    const turn = i === null ? undefined : turns[i];
    const ops = turn && !turn.applied ? turn.result?.ops : undefined;
    if (!ops?.length) {
      setShown(null);
      onPreview.current?.(null, undefined);
      return;
    }
    setShown({ i: i!, pinned });
    onPreview.current?.(ops, turn!.result!.sheetId);
  };
  // Leaving the panel (section folded, signed out) ends the preview.
  useEffect(() => () => onPreview.current?.(null, undefined), []);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  /** The reply as the model writes it (streamed), shown until the proposal is checked. */
  const [partial, setPartial] = useState('');
  const [error, setError] = useState<string | null>(null);
  const log = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight });
  }, [turns, busy]);

  // Dictation: idle → recording (seconds counted) → transcribing → text in the box.
  const [voice, setVoice] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [seconds, setSeconds] = useState(0);
  const recording = useRef<Recording | null>(null);
  const canDictate = ai.canTranscribe && micSupported();
  const stopDictation = async () => {
    const rec = recording.current;
    if (!rec) return;
    recording.current = null;
    setVoice('transcribing');
    try {
      const wav = await rec.stop();
      if (!wav) {
        setError(t('ai.nothingHeard'));
        return;
      }
      const said = await ai.transcribe(wav, props.locale);
      if (!said) {
        setError(t('ai.nothingHeard'));
        return;
      }
      setText((x) => (x.trim() ? `${x.trimEnd()} ${said}` : said));
      input.current?.focus();
    } catch (e) {
      setError(isAiUnavailable(e) ? t('ai.unavailable') : t('ai.error', { msg: (e as Error).message }));
    } finally {
      setVoice('idle');
    }
  };
  const startDictation = async () => {
    setError(null);
    try {
      recording.current = await startRecording();
    } catch (e) {
      const name = (e as Error).name;
      setError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? t('ai.micDenied')
          : name === 'NotFoundError'
            ? t('ai.micMissing')
            : t('ai.error', { msg: (e as Error).message }),
      );
      return;
    }
    setSeconds(0);
    setVoice('recording');
  };
  useEffect(() => {
    if (voice !== 'recording') return;
    const started = Date.now();
    const timer = setInterval(() => {
      const s = Math.floor((Date.now() - started) / 1000);
      setSeconds(s);
      if (s >= MAX_SECONDS) void stopDictation();
    }, 250);
    return () => clearInterval(timer);
    // Runs per recording; stopDictation only reads refs and stable setters.
  }, [voice]);
  // Closing the dialog while recording releases the microphone.
  useEffect(() => () => recording.current?.cancel(), []);

  const send = async (asked?: string, fix?: FixTarget) => {
    const q = (asked ?? text).trim();
    if (!q || busy) return;
    const next: Turn[] = [...turns, { role: 'user', text: q, ...(fix ? { fix } : {}) }];
    setTurns(next);
    if (asked === undefined) setText('');
    setBusy(true);
    setError(null);
    try {
      const messages: AssistMessage[] = next.map((x) => ({
        role: x.role,
        text: x.text,
        ...(x.fix ? { fix: x.fix } : {}),
      }));
      const asked = props.project;
      const r = await assistWithCheck(
        (m) => {
          setPartial('');
          return ai.assist(m, props.project, props.locale, setPartial);
        },
        props.project,
        messages,
        props.sheetId,
        ai.mode !== 'demo',
        props.locale,
      );
      setTurns([
        ...next,
        {
          role: 'assistant',
          text: r.reply,
          ...(r.actions.length ? { result: r.result, findings: r.findings, actions: r.actions } : {}),
          source: { messages, context: projectContext(asked, props.locale), project: asked },
        },
      ]);
    } catch (e) {
      setError(isAiUnavailable(e) ? t('ai.unavailable') : t('ai.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
      setPartial('');
    }
  };
  // A finding's "Fix with the assistant": asked as if typed.
  const lastRequest = useRef(0);
  useEffect(() => {
    if (!props.request || props.request.n === lastRequest.current) return;
    lastRequest.current = props.request.n;
    void send(props.request.text, props.request.fix);
    // send reads the current turns; only a new request should trigger it.
  }, [props.request]);
  /** Keep or leave out one line of a proposal: the rest is computed and checked again. */
  const toggle = (i: number, action: number) => {
    const turn = turns[i]!;
    if (!turn.actions || turn.applied) return;
    const excluded = turn.excluded?.includes(action)
      ? turn.excluded.filter((x) => x !== action)
      : [...(turn.excluded ?? []), action];
    const kept = turn.actions.filter((_, k) => !excluded.includes(k));
    const result = actionsToOps(props.project, kept, props.sheetId, props.locale);
    // Summary lines of the left-out actions stay listed (unticked), from the full proposal.
    const full = actionsToOps(props.project, turn.actions, props.sheetId, props.locale);
    const next = {
      ...turn,
      excluded,
      result: { ...result, summary: full.summary, actionOf: full.actionOf },
      findings: checkProposal(props.project, result),
    };
    setTurns(turns.map((x, j) => (j === i ? next : x)));
    if (shown?.i === i) onPreview.current?.(result.ops.length ? result.ops : null, result.sheetId);
  };
  const apply = (i: number) => {
    const turn = turns[i]!;
    if (!turn.result?.ops.length) return;
    setShown(null);
    props.onApply(turn.result.ops, turn.result.sheetId);
    // The project changed: older proposals are based on a stale project.
    setTurns(
      turns.map((x, j) =>
        j === i ? { ...x, applied: true } : x.result && !x.applied ? { ...x, result: undefined } : x,
      ),
    );
  };

  return (
    <div className="assistant-panel" data-testid="assistant-panel">
      {ai.mode === 'demo' && (
        <p className="notice" data-testid="ai-demo">
          {t('ai.demoText')}
        </p>
      )}
      <div className="chat" ref={log} data-testid="assist-log">
        {turns.length === 0 && !busy && <p className="muted small">{t('side.assistHint')}</p>}
        {turns.map((x, i) => (
          <div key={i} className={`bubble ${x.role}`}>
            <p>{x.text}</p>
            {x.result && (
              <div
                className={`proposal${shown?.i === i ? ' previewing' : ''}`}
                onMouseEnter={() => !shown?.pinned && show(i)}
                onMouseLeave={() => !shown?.pinned && show(null)}
                onFocus={() => !shown?.pinned && show(i)}
                onBlur={(e) => {
                  if (!shown?.pinned && !e.currentTarget.contains(e.relatedTarget as Node | null)) show(null);
                }}
                data-testid="assist-proposal"
              >
                {x.result.summary.length > 0 && (
                  <>
                    <strong>{t('ai.proposal')}</strong>
                    <ul className="proposal-lines">
                      {x.result.summary.map((s, k) => {
                        const action = x.result!.actionOf[k] ?? k;
                        const kept = !x.excluded?.includes(action);
                        return x.actions && x.result!.summary.length > 1 && !x.applied ? (
                          <li key={k} className={kept ? '' : 'left-out'}>
                            <label>
                              <input
                                type="checkbox"
                                checked={kept}
                                onChange={() => toggle(i, action)}
                                data-testid={`assist-line-${k}`}
                              />{' '}
                              {s}
                            </label>
                          </li>
                        ) : (
                          <li key={k} className={kept ? '' : 'left-out'}>
                            {s}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
                {!x.applied && x.findings && x.findings.length > 0 && (
                  <div className="warn" data-testid="assist-findings">
                    <strong>
                      {say(
                        props.uiLocale ?? props.locale,
                        'À vérifier avant d’appliquer :',
                        'Check before applying:',
                        'Controleer vóór het toepassen:',
                        'Vor dem Anwenden prüfen:',
                      )}
                    </strong>
                    <ul>
                      {x.findings.map((f, k) => (
                        <li key={k}>{pickText(f.message, props.uiLocale ?? props.locale)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {x.result.skipped.map((s, k) => (
                  <p key={k} className="warn">
                    {t('ai.skipped', { msg: s })}
                  </p>
                ))}
                {x.applied ? (
                  <p className="small">{t('ai.applied', { mod: props.mod })}</p>
                ) : (
                  x.result.ops.length > 0 && (
                    <div className="proposal-actions">
                      <button
                        className={shown?.i === i && shown.pinned ? 'pressed' : ''}
                        aria-pressed={shown?.i === i && shown.pinned}
                        onClick={() => (shown?.i === i && shown.pinned ? show(null) : show(i, true))}
                        title={t('ai.previewHint')}
                        data-testid="assist-preview"
                      >
                        {t('ai.preview')}
                      </button>
                      <button className="primary" onClick={() => apply(i)} data-testid="assist-apply">
                        {t('ai.apply')}
                      </button>
                    </div>
                  )
                )}
              </div>
            )}
            {x.role === 'assistant' && x.source && ai.mode !== 'demo' && (
              <FeedbackBar
                t={t}
                send={(rating, comment, consent) =>
                  ai.sendFeedback({
                    rating,
                    comment,
                    locale: props.locale,
                    ...(props.app ? { app: props.app } : {}),
                    ...(consent ? { shared: sharedAnswer(x), consent } : {}),
                  })
                }
                share={(id, consent) => ai.shareFeedback(id, sharedAnswer(x), consent)}
              />
            )}
          </div>
        ))}
        {busy && (
          <div className="bubble assistant muted" aria-live="polite" data-testid="assist-partial">
            {partial || t('ai.thinking')}
          </div>
        )}
      </div>
      {error && <p className="warn">{error}</p>}
      <div className="chat-input">
        <textarea
          ref={input}
          rows={2}
          value={text}
          placeholder={t('ai.askHint')}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          data-testid="assist-text"
        />
        {canDictate && (
          <button
            className={`mic${voice === 'recording' ? ' recording' : ''}`}
            onClick={() => void (voice === 'recording' ? stopDictation() : startDictation())}
            disabled={voice === 'transcribing'}
            title={
              voice === 'recording'
                ? t('ai.stopDictation', { s: seconds })
                : voice === 'transcribing'
                  ? t('ai.transcribing')
                  : t('ai.dictate')
            }
            aria-label={voice === 'recording' ? t('ai.stopDictation', { s: seconds }) : t('ai.dictate')}
            aria-pressed={voice === 'recording'}
            data-testid="assist-mic"
          >
            <Icon name={voice === 'recording' ? 'stop' : 'mic'} />
            {voice === 'recording' && <span className="mic-time">{seconds} s</span>}
            {voice === 'transcribing' && <span className="mic-time">…</span>}
          </button>
        )}
        <button
          className="primary"
          onClick={() => void send()}
          disabled={busy || !text.trim() || voice !== 'idle'}
          data-testid="assist-send"
        >
          {t('ai.send')}
        </button>
      </div>
    </div>
  );
}

const MEDIA: Record<string, ImageMediaType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/** Hand sketch → single-line diagram on a new sheet. */
export function SketchDialog(props: {
  ai: AiService;
  platform: Platform;
  t: Translate;
  onClose(): void;
  onCreate(board: ReturnType<typeof sketchToBoard>): void;
}) {
  const { ai, t } = props;
  const [img, setImg] = useState<{ url: string; bytes: Uint8Array; mime: ImageMediaType } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sketch, setSketch] = useState<Sketch | null>(null);
  const pick = async () => {
    const f = await props.platform.openFile({ extensions: Object.keys(MEDIA), description: 'Image' });
    if (!f) return;
    // Smaller image: much faster for the model, and a lighter upload from the site.
    const small = await prepareAiImage(
      f.bytes,
      MEDIA[f.name.toLowerCase().split('.').pop() ?? ''] ?? 'image/jpeg',
    );
    setImg({
      url: URL.createObjectURL(new Blob([small.bytes.slice().buffer as ArrayBuffer], { type: small.mime })),
      bytes: small.bytes,
      mime: small.mime,
    });
    setSketch(null);
  };
  const read = async () => {
    setBusy(true);
    setError(null);
    try {
      setSketch(await ai.readSketch(img?.bytes ?? new Uint8Array(), img?.mime ?? 'image/jpeg'));
    } catch (e) {
      setError(isAiUnavailable(e) ? t('ai.unavailable') : t('ai.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  };
  const n = sketch?.groups.reduce((s, g) => s + g.circuits.length, 0) ?? 0;
  return (
    <Dialog
      title={t('ai.sketch').replace('…', '')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          {sketch ? (
            <button
              className="primary"
              onClick={() => props.onCreate(sketchToBoard(sketch))}
              disabled={!n}
              data-testid="sketch-create"
            >
              {t('ai.import')}
            </button>
          ) : (
            <button
              className="primary"
              onClick={read}
              disabled={busy || (!img && ai.mode === 'remote')}
              data-testid="sketch-read"
            >
              {busy ? t('ai.analysing') : t('ai.analyse')}
            </button>
          )}
        </>
      }
    >
      {ai.mode === 'demo' && (
        <p className="notice" data-testid="ai-demo">
          {t('ai.demoBanner')}
        </p>
      )}
      <p className="small">{t('ai.sketchHint')}</p>
      <div className="ai-photo">
        {img ? <img src={img.url} alt="" /> : <div className="ai-placeholder" />}
        <button onClick={pick}>{t('ai.chooseSketch')}</button>
      </div>
      {error && <p className="warn">{error}</p>}
      {sketch && (
        <>
          <p data-testid="sketch-summary">{t('ai.sketchResult', { n, g: sketch.groups.length })}</p>
          {sketch.mainRcdMa && (
            <p className="small">
              {t('ai.mainRcd')} : {sketch.mainRcdMa} mA
            </p>
          )}
          {sketch.groups.map((g, i) => (
            <div key={i}>
              <strong>
                {g.rcdMa ? `${g.rcdMa} mA${g.rcdType ? ` type ${g.rcdType}` : ''}` : t('ai.mainRcd')}
              </strong>
              <ul className="described">
                {g.circuits.map((c, k) => (
                  <li key={k}>
                    {c.label || '?'} — {c.curve ?? ''}
                    {c.ratingA ?? '?'} A
                    {c.cable ? ` · ${c.cable.type} ${c.cable.cores}G${c.cable.sectionMm2}` : ''} ·{' '}
                    {c.points.map((p) => `${p.count}× ${p.symbolId ?? p.kind}`).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {sketch.notes && <p className="small">{sketch.notes}</p>}
        </>
      )}
    </Dialog>
  );
}

/** What a shared answer holds: the request, the answer and the project without personal data. */
function sharedAnswer(x: Turn): SharedAnswer {
  return {
    messages: x.source!.messages,
    context: x.source!.context,
    reply: x.text,
    actions: x.actions ?? [],
    summary: x.result?.summary ?? [],
    skipped: x.result?.skipped ?? [],
    findings: (x.findings ?? []).map((f) => `${f.ruleId}: ${f.message.en}`),
    project: redactProject(x.source!.project),
  };
}

/**
 * The two consent boxes: share (a test case), then, only with it, also train Tablo's own model.
 * Both unticked by default.
 */
function ShareBoxes(props: {
  t: Translate;
  consent: FeedbackConsent | null;
  onChange(consent: FeedbackConsent | null): void;
}) {
  const { t, consent } = props;
  return (
    <>
      <label className="small">
        <input
          type="checkbox"
          checked={consent !== null}
          onChange={(e) => props.onChange(e.target.checked ? 'test' : null)}
          data-testid="assist-feedback-share"
        />{' '}
        {t('ai.fbShare')}
      </label>
      <label className={`small ${consent === null ? 'muted' : ''}`}>
        <input
          type="checkbox"
          checked={consent === 'test+train'}
          disabled={consent === null}
          onChange={(e) => props.onChange(e.target.checked ? 'test+train' : 'test')}
          data-testid="assist-feedback-train"
        />{' '}
        {t('ai.fbTrain')}
      </label>
      <p className="small muted">{t('ai.fbShareHint')}</p>
    </>
  );
}

/**
 * 👍/👎 under an answer. 👍 sends only the rating, at once, then offers to share the answer as a
 * good example. 👎 asks what went wrong. Either way the conversation and the project (without
 * personal data) are shared only if the user ticks the box, and used for training only with the
 * second box.
 */
function FeedbackBar(props: {
  t: Translate;
  /** Resolves to the feedback's id. */
  send(rating: 1 | -1, comment: string, consent: FeedbackConsent | null): Promise<string>;
  share(id: string, consent: FeedbackConsent): Promise<void>;
}) {
  const { t } = props;
  const [mode, setMode] = useState<'idle' | 'down' | 'up-sent' | 'up-share' | 'sent'>('idle');
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [consent, setConsent] = useState<FeedbackConsent | null>(null);
  const [upId, setUpId] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Runs a send; on success goes to `next` (or what it returns), else shows the error. */
  const run = async (task: () => Promise<typeof mode>) => {
    setBusy(true);
    setError(null);
    try {
      setMode(await task());
    } catch (e) {
      setError(t('ai.fbError', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  };
  const up = () =>
    run(async () => {
      const id = await props.send(1, '', null);
      setUpId(id);
      return id ? 'up-sent' : 'sent';
    });
  const down = () =>
    run(async () => {
      await props.send(-1, comment, consent);
      return 'sent';
    });
  const shareUp = () =>
    run(async () => {
      if (consent) await props.share(upId, consent);
      return 'sent';
    });
  if (mode === 'sent')
    return (
      <p className="small muted feedback-done" data-testid="assist-feedback-done">
        {t('ai.fbThanks')}
      </p>
    );
  if (mode === 'up-sent')
    return (
      <p className="small muted feedback-done" data-testid="assist-feedback-done">
        {t('ai.fbThanks')}{' '}
        <button
          className="link"
          onClick={() => {
            setConsent(null);
            setMode('up-share');
          }}
          data-testid="assist-feedback-share-good"
        >
          {t('ai.fbShareGood')}
        </button>
      </p>
    );
  return (
    <div className="feedback" data-testid="assist-feedback">
      {mode === 'idle' && (
        <div className="feedback-buttons">
          <button
            className="icon-btn"
            onClick={() => void up()}
            disabled={busy}
            title={t('ai.fbUp')}
            aria-label={t('ai.fbUp')}
            data-testid="assist-feedback-up"
          >
            👍
          </button>
          <button
            className="icon-btn"
            onClick={() => setMode('down')}
            disabled={busy}
            title={t('ai.fbDown')}
            aria-label={t('ai.fbDown')}
            data-testid="assist-feedback-down"
          >
            👎
          </button>
        </div>
      )}
      {mode === 'down' && (
        <div className="feedback-form">
          <textarea
            rows={2}
            value={comment}
            placeholder={t('ai.fbWhat')}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            data-testid="assist-feedback-comment"
          />
          <ShareBoxes t={t} consent={consent} onChange={setConsent} />
          <div className="proposal-actions">
            <button onClick={() => setMode('idle')}>{t('ai.fbCancel')}</button>
            <button
              className="primary"
              onClick={() => void down()}
              disabled={busy}
              data-testid="assist-feedback-send"
            >
              {t('ai.fbSend')}
            </button>
          </div>
        </div>
      )}
      {mode === 'up-share' && (
        <div className="feedback-form">
          <ShareBoxes t={t} consent={consent} onChange={setConsent} />
          <div className="proposal-actions">
            <button onClick={() => setMode('sent')}>{t('ai.fbCancel')}</button>
            <button
              className="primary"
              onClick={() => void shareUp()}
              disabled={busy || !consent}
              data-testid="assist-feedback-send"
            >
              {t('ai.fbSend')}
            </button>
          </div>
        </div>
      )}
      {error && <p className="warn small">{error}</p>}
    </div>
  );
}
